import { Badge, Box, Button, Group, SimpleGrid, Skeleton, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconCheck } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';

import { meApiSlice, TaskOfferDto, useAcceptTaskOfferMutation } from '@/store/slices/api/meApiSlice';
import { useGetTaskQuery } from '@/store/slices/api/tasksApiSlice';
import { useAppDispatch } from '@/store/hooks';
import { CATEGORY_COLOR, CATEGORY_LABEL } from '@/utils/taskConstants';
import routes from '@/routes/config';

function DetailCell({ label, value }: { label: string; value: string }) {
  return (
    <Box>
      <Text size="10px" tt="uppercase" c="plum.5" fw={600} lh={1.2} mb={2} style={{ letterSpacing: '0.04em' }}>
        {label}
      </Text>
      <Text size="xs" fw={600} c="plum.9" lh={1.3}>
        {value}
      </Text>
    </Box>
  );
}

function formatAlertType(value?: string | null): string {
  if (!value || value.toLowerCase() === 'none') return 'None';
  const n = value.toLowerCase();
  if (n === 'red') return 'Red';
  if (n === 'yellow') return 'Yellow';
  if (n === 'routine') return 'Routine';
  return value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatAppointmentType(value?: string | null): string {
  if (!value) return '—';
  return value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatDeviceType(value?: string | null): string {
  if (!value) return '—';
  return value.toUpperCase();
}

function formatDeviceVendor(value?: string | null): string {
  if (!value) return '—';
  return value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function elapsedLabel(offeredAt: string): string {
  const ms = Date.now() - new Date(offeredAt).getTime();
  const mins = Math.floor(ms / 60_000);
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  return `${hrs}h`;
}

interface TaskOfferCardProps {
  offer: TaskOfferDto;
}

export function TaskOfferCard({ offer }: TaskOfferCardProps) {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { data: task, isLoading } = useGetTaskQuery(offer.taskId);
  const [acceptOffer, { isLoading: isAccepting }] = useAcceptTaskOfferMutation();

  if (isLoading || !task) {
    return (
      <Box style={{ border: '1px solid var(--mantine-color-gray-3)', borderRadius: 10, padding: 12 }}>
        <Skeleton height={12} mb={8} radius="sm" />
        <Skeleton height={10} width="60%" radius="sm" />
      </Box>
    );
  }

  const isClinical = task.workType === 'clinical';
  const isReport = isClinical && !!task.transmissionReportId;
  const orgName = task.organization?.name ?? task.subject;
  const elapsed = elapsedLabel(offer.offeredAt);
  const priority = task.priorityRank;

  const taskDest = isReport
    ? routes.physician.transmissionReportDetail(task.transmissionReportId!)
    : routes.physician.taskDetail(task.id);

  const handleTake = async () => {
    try {
      await acceptOffer({ taskId: task.id, expectedRouteGeneration: task.routeGeneration }).unwrap();
      dispatch(meApiSlice.endpoints.getMe.initiate(undefined, { forceRefetch: true }));
      navigate(taskDest);
    } catch {
      notifications.show({ title: 'Could not take task', message: 'The offer may have expired or already been accepted.', color: 'red' });
    }
  };

  // const handlePreview = () => {
  //   navigate(taskDest);
  // };

  return (
    <Box
      style={{
        border: '1px solid #C5B3E8',
        background: '#F3EFFF',
        borderRadius: 10,
        padding: 12,
      }}>
      <Group justify="space-between" wrap="nowrap" mb={6} gap={6}>
        <Group gap={6} wrap="nowrap" style={{ minWidth: 0 }}>
          <Badge size="xs" radius="sm" variant="light" color={CATEGORY_COLOR[task.workType]}>
            {CATEGORY_LABEL[task.workType]}
          </Badge>
          <Text size="xs" c="plum.5" truncate>
            {task.taskNumber}
          </Text>
        </Group>
        {priority != null ? (
          <Badge size="xs" radius="sm" variant="outline" color="gray">
            P{priority}
          </Badge>
        ) : (
          <Text size="xs" c="plum.5" style={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
            {elapsed}
          </Text>
        )}
      </Group>

      <Text size="sm" fw={700} c="plum.9" lh={1.25} mb={8}>
        {orgName}
      </Text>

      {isReport ? (
        <SimpleGrid cols={2} spacing={8} verticalSpacing={8} mb={10}>
          <DetailCell label="Alert type" value={formatAlertType(task.alertType)} />
          <DetailCell label="Appointment" value={formatAppointmentType(task.appointmentType)} />
          <DetailCell label="Device type" value={formatDeviceType(task.deviceType)} />
          <DetailCell label="Device vendor" value={formatDeviceVendor(task.deviceVendor)} />
        </SimpleGrid>
      ) : (
        <Box mb={8}>
          <DetailCell label="Admin task" value={task.taskType?.name ?? '—'} />
        </Box>
      )}

      {task.assignee && (
        <Text size="xs" c="plum.5" mb={8}>
          {[task.assignee.given, task.assignee.family].filter(Boolean).join(' ')} · {elapsed}
        </Text>
      )}

      <Group gap={6} grow>
        {/* <Button
          variant="default"
          size="xs"
          leftSection={<IconEye size={13} />}
          onClick={handlePreview}>
          Preview
        </Button> */}
        <Button
          variant="light"
          color="plum"
          size="xs"
          leftSection={<IconCheck size={13} />}
          loading={isAccepting}
          onClick={handleTake}>
          Take
        </Button>
      </Group>
    </Box>
  );
}
